import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { formatDistanceToNow, differenceInHours, differenceInMinutes, format } from 'date-fns';

const AsyncDeadlineBanner = ({ deadline, onDeadlineExpired }) => {
  const [timeRemaining, setTimeRemaining] = useState('');
  const [urgency, setUrgency] = useState('normal'); // 'normal', 'warning', 'critical'
  const [progress, setProgress] = useState(100);
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    if (!deadline) return;

    const deadlineDate = new Date(deadline);

    const updateTimer = () => {
      const now = new Date();
      const diff = deadlineDate - now;

      if (diff <= 0) {
        setTimeRemaining('Voting has ended');
        setUrgency('critical');
        setProgress(0);
        setIsExpired(true);
        if (onDeadlineExpired) {
          onDeadlineExpired();
        }
        return;
      }

      // Calculate urgency based on time remaining
      const hoursRemaining = differenceInHours(deadlineDate, now);
      const minutesRemaining = differenceInMinutes(deadlineDate, now);

      if (hoursRemaining < 1) {
        setUrgency('critical');
      } else if (hoursRemaining < 6) {
        setUrgency('warning');
      } else {
        setUrgency('normal');
      }

      // Format time remaining
      if (minutesRemaining < 60) {
        setTimeRemaining(`${minutesRemaining} minutes remaining`);
      } else {
        setTimeRemaining(formatDistanceToNow(deadlineDate, { addSuffix: false }) + ' remaining');
      }

      // Calculate progress (assuming a 7-day max period for the progress bar)
      const maxDuration = 7 * 24 * 60 * 60 * 1000; // 7 days in ms
      const progressValue = Math.max(0, Math.min(100, (diff / maxDuration) * 100));
      setProgress(progressValue);
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000 * 60); // Update every minute

    return () => clearInterval(interval);
  }, [deadline, onDeadlineExpired]);

  if (!deadline) return null;

  const getUrgencyStyles = () => {
    switch (urgency) {
      case 'critical':
        return {
          bgcolor: 'error.light',
          borderColor: 'error.main',
          textColor: 'error.contrastText',
          iconColor: 'error.dark'
        };
      case 'warning':
        return {
          bgcolor: 'warning.light',
          borderColor: 'warning.main',
          textColor: 'warning.contrastText',
          iconColor: 'warning.dark'
        };
      default:
        return {
          bgcolor: 'info.light',
          borderColor: 'info.main',
          textColor: 'info.contrastText',
          iconColor: 'info.dark'
        };
    }
  };

  const styles = getUrgencyStyles();
  const deadlineDate = new Date(deadline);

  return (
    <Paper
      elevation={2}
      sx={{
        p: 2,
        mb: 2,
        bgcolor: styles.bgcolor,
        borderLeft: '4px solid',
        borderColor: styles.borderColor
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        {urgency === 'critical' ? (
          <WarningAmberIcon sx={{ color: styles.iconColor }} />
        ) : (
          <AccessTimeIcon sx={{ color: styles.iconColor }} />
        )}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ color: styles.textColor }}>
          {isExpired ? 'Deadline Passed' : 'Voting Deadline'}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="body2" sx={{ color: styles.textColor }}>
          {timeRemaining}
        </Typography>
        <Typography variant="caption" sx={{ color: styles.textColor }}>
          {format(deadlineDate, 'MMM d, yyyy h:mm a')}
        </Typography>
      </Box>

      {!isExpired && (
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 6,
            borderRadius: 3,
            bgcolor: 'rgba(255,255,255,0.3)',
            '& .MuiLinearProgress-bar': {
              bgcolor: styles.borderColor
            }
          }}
        />
      )}
    </Paper>
  );
};

export default AsyncDeadlineBanner;
